import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleGenericError } from '../handle-generic-error';

export const authenticateUrlAccessCode = createAsyncThunk(
  'auth/authenticate-url-access-code',
  async ({ code, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    await axios
      .get(`${zensignApiUrl}/external-assignee/verify/${code}`)
      .then((response) => {
        thunkAPI.dispatch(authSlice.actions.urlAccessCodeAuthenticated({ code: code, ...response.data }));
      })
      .catch((err) => {
        handleGenericError(thunkAPI.dispatch, err, navigate);
        throw thunkAPI.rejectWithValue({ itemAssignmentCodeAuthenticationStatus: 'Invalid', ...err.response.data });
      });
  }
);

export const authenticateForPdfUrlAccessCode = createAsyncThunk(
  'auth/authenticate-for-pdf-url-access-code',
  async ({ code, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    try {
      const response = await axios.get(`${zensignApiUrl}/external-assignee/verify/pdfs/${code}`);
      thunkAPI.dispatch(authSlice.actions.urlAccessCodeAuthenticated({ code: code, ...response.data }));
    } catch (err) {
      handleGenericError(thunkAPI.dispatch, err, navigate);
      throw thunkAPI.rejectWithValue({ itemAssignmentCodeAuthenticationStatus: 'Invalid', ...err.response.data });
    }
  }
);

export const authenticateEmail = createAsyncThunk(
  'auth/authenticate-email',
  async ({ email, code, navigate }, thunkAPI) => {
    if (!email) {
      throw thunkAPI.rejectWithValue({ emailAuthenticationStatus: 'Invalid email', errors: ['Email is required'] });
    }
    const normalizedEmail = email.toLowerCase().trim();
    const zensignApiUrl = window.config.zensignApiUrl;
    await axios
      .post(`${zensignApiUrl}/external-assignee/verify/email`, { email: normalizedEmail, code: code })
      .then((response) => {
        thunkAPI.dispatch(authSlice.actions.emailAuthenticated({ email: normalizedEmail, ...response.data }));
      })
      .catch((err) => {
        switch (err.response.status) {
          case 422:
            throw thunkAPI.rejectWithValue({ emailAuthenticationStatus: 'Invalid email', ...err.response.data });
          case 401:
            throw thunkAPI.rejectWithValue({ emailAuthenticationStatus: 'Invalid URL', ...err.response.data });
          default:
            handleGenericError(thunkAPI.dispatch, err, navigate);
            throw thunkAPI.rejectWithValue({ ...err.response.data });
        }
      });
  }
);

export const resendNewSecurityCode = createAsyncThunk(
  'auth/resend-new-security-code',
  async ({ code, type, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    await axios
      .post(`${zensignApiUrl}/external-assignee/verify/resend-new-security-code`, { code: code, type: type })
      .then((response) => {
        thunkAPI.dispatch(authSlice.actions.resendNewSecurityCode({ ...response.data }));
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          thunkAPI.dispatch(authSlice.actions.sessionExpiredDetected());
          throw thunkAPI.rejectWithValue({ ...err.response.data });
        }
        handleGenericError(thunkAPI.dispatch, err, navigate);
        throw thunkAPI.rejectWithValue({ ...err.response.data });
      });
  }
);

export const syncWithSession = createAsyncThunk('auth/sync-with-session', async (_, thunkAPI) => {
  const zensignApiUrl = window.config.zensignApiUrl;
  await axios
    .get(`${zensignApiUrl}/session`)
    .then((response) => {
      thunkAPI.dispatch(authSlice.actions.sessionDataReceived(response.data));
    })
    .catch((err) => {
      thunkAPI.dispatch(authSlice.actions.sessionSyncError(err.message));
    });
});

export const extendSessionExpiry = createAsyncThunk('auth/extend-session-expiry', async (_, thunkAPI) => {
  const zensignApiUrl = window.config.zensignApiUrl;
  await axios
    .put(`${zensignApiUrl}/session`)
    .then(() => {
      thunkAPI.dispatch(authSlice.actions.sessionExpiryExtended());
    })
    .catch((err) => {
      thunkAPI.dispatch(authSlice.actions.sessionSyncError(err.message));
    });
});

export const logoutUser = createAsyncThunk('auth/logout-user', async (_, thunkAPI) => {
  const zensignApiUrl = window.config.zensignApiUrl;
  await axios
    .delete(`${zensignApiUrl}/session`)
    .then(() => {
      thunkAPI.dispatch(authSlice.actions.userLoggedOut());
    })
    .catch((err) => {
      thunkAPI.dispatch(authSlice.actions.sessionSyncError(err.message));
    });
});

export const authenticateSecurityCode = createAsyncThunk(
  'auth/authenticate-otp',
  async ({ code, securityCode, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    await axios
      .post(`${zensignApiUrl}/external-assignee/verify/securityCode`, {
        code: code,
        securityCode: securityCode,
      })
      .then((response) => {
        thunkAPI.dispatch(authSlice.actions.secretCodeAuthenticated({ securityCode: securityCode, ...response.data }));
        thunkAPI.dispatch(syncWithSession());
      })
      .catch((err) => {
        switch (err.response.status) {
          case 401:
            thunkAPI.dispatch(authSlice.actions.sessionExpiredDetected());
            throw thunkAPI.rejectWithValue({
              securityCodeAuthenticationStatus: 'Session expired',
              ...err.response.data,
            });
          case 422:
            throw thunkAPI.rejectWithValue({
              securityCodeAuthenticationStatus: 'Invalid security code',
              ...err.response.data,
            });
          case 404:
            throw thunkAPI.rejectWithValue({ securityCodeAuthenticationStatus: 'URL Invalid', ...err.response.data });
          default:
            handleGenericError(thunkAPI.dispatch, err, navigate);
            throw thunkAPI.rejectWithValue({ ...err.response.data });
        }
      });
  }
);

export const authenticatePdfUrlAccessCode = createAsyncThunk(
  'auth/authenticate-pdf-url-access-code',
  async ({ code, navigate }, thunkAPI) => {
    const zensignApiUrl = window.config.zensignApiUrl;
    await axios
      .get(`${zensignApiUrl}/pdfs/${code}`)
      .then((response) => {
        thunkAPI.dispatch(authSlice.actions.pdfUrlAccessCodeAuthenticated(response.data));
      })
      .catch((err) => {
        handleGenericError(thunkAPI.dispatch, err, navigate);
        throw thunkAPI.rejectWithValue({ ...err.response.data });
      });
  }
);

export const initialState = {
  user: {
    itemAssignmentCodeAuthenticated: false,
    itemAssignmentCodeAuthenticationStatus: 'Unauthenticated',
    itemAssignmentCode: '',
    itemAssigneeEmail: '',
    email: '',
    emailAuthenticated: false,
    emailAuthenticationStatus: '',
    emailAuthenticationErrors: [],
    securityCode: '',
    secretCodeAuthenticated: false,
    securityCodeAuthenticationStatus: '',
    pdfUrlCodeAuthenticated: false,
    pdfObjectUrl: '',
    logoUrl: '',
    type: '',
    pdfIdentifier: '',
    sessionExpired: false,
  },
  session: {
    syncCompleted: false,
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    urlAccessCodeAuthenticated: (state, action) => {
      const newUserState = {
        ...state.user,
        itemAssignmentCode: action.payload.data.identifier,
        itemAssigneeEmail: action.payload.data.identifierAssigneeEmail,
        itemAssignmentCodeAuthenticated: true,
        type: action.payload.data.type,
        pdfIdentifier: action.payload.data.pdfIdentifier,
      };
      if (newUserState.itemAssigneeEmail !== state.user.email) {
        newUserState.emailAuthenticated = false;
        newUserState.secretCodeAuthenticated = false;
        delete newUserState.email;
      }
      return {
        ...state,
        user: newUserState,
      };
    },
    emailAuthenticated: (state, action) => {
      return {
        ...state,
        user: {
          ...state.user,
          email: action.payload.email,
          emailAuthenticated: true,
        },
      };
    },
    resendNewSecurityCode: (state) => {
      return { ...state };
    },
    secretCodeAuthenticated: (state, action) => {
      return {
        ...state,
        user: {
          ...state.user,
          securityCode: action.payload.securityCode,
          secretCodeAuthenticated: true,
        },
      };
    },
    pdfUrlAccessCodeAuthenticated: (state, action) => {
      return {
        ...state,
        user: {
          ...state.user,
          pdfObjectUrl: action.payload.data.objectUrl,
          pdfUrlCodeAuthenticated: true,
        },
      };
    },
    sessionDataReceived: (state, action) => {
      const newState = { ...state };
      const sessionState = action.payload;
      const itemIdentifier = ((state.user || {}).itemAssignmentCode || '').trim();
      const userEmail = ((state.user || {}).email || '').trim();
      const userEmailConflict = userEmail !== '' && userEmail !== sessionState.email;
      const sessionConflict = userEmailConflict;
      newState.session = {
        ...newState.session,
        syncCompleted: true,
      };
      newState.user = {
        ...newState.user,
        itemAssignmentCode: sessionState.identifier,
        emailAuthenticated: false,
      };
      if (sessionConflict) {
        newState.user = {
          ...initialState.user,
          itemAssignmentCode: itemIdentifier,
        };
        newState.session = {
          ...initialState.session,
          syncCompleted: true,
          conflict: true,
        };
        return newState;
      }
      if (sessionState.authenticated) {
        newState.user = {
          ...newState.user,
          itemAssignmentCodeAuthenticated: true,
          emailAuthenticated: true,
          secretCodeAuthenticated: true,
          itemAssignmentCode: sessionState.identifier,
          email: sessionState.email,
          securityCode: sessionState.securityCode,
          logoUrl: sessionState.memberLogoUrl,
          type: sessionState.type,
        };
        newState.session = {
          ...newState.session,
          syncCompleted: true,
          signatoryEmail: sessionState.signatoryEmail,
        };
        return newState;
      }
      return newState;
    },
    sessionExpiryExtended: (state) => {
      return { ...state };
    },
    sessionExpiredDetected: (state) => {
      return { ...state, user: { ...state.user, sessionExpired: true } };
    },
    userLoggedOut: () => {
      return { ...initialState, session: { ...initialState.session, syncCompleted: true } };
    },
    sessionConflict: (_, action) => {
      return {
        ...initialState,
        session: {
          ...initialState.session,
          syncCompleted: true,
          conflict: true,
          conflictedItemAssignmentCode: action.payload,
        },
      };
    },
    sessionSyncError: (state) => {
      return { ...state, session: { ...state.session, syncCompleted: true } };
    },
    signatoryEmailReceived: (state, action) => {
      return {
        ...state,
        session: {
          ...state.session,
          signatoryEmail: action.payload,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticateUrlAccessCode.fulfilled, (state) => {
        state.user.itemAssignmentCodeAuthenticationStatus = 'Success';
      })
      .addCase(authenticateUrlAccessCode.rejected, (state, action) => {
        state.user.itemAssignmentCodeAuthenticationStatus = action.payload?.itemAssignmentCodeAuthenticationStatus;
      })
      .addCase(authenticateUrlAccessCode.pending, (state, action) => {
        state.user.itemAssignmentCodeAuthenticationStatus = 'Pending';
        state.user.itemAssignmentCodeAuthenticated = false;
        state.user.itemAssignmentCode = action.meta.arg.code;
      })
      .addCase(authenticateForPdfUrlAccessCode.fulfilled, (state) => {
        state.user.itemAssignmentCodeAuthenticationStatus = 'Success';
      })
      .addCase(authenticateForPdfUrlAccessCode.rejected, (state, action) => {
        state.user.itemAssignmentCodeAuthenticationStatus = action.payload?.itemAssignmentCodeAuthenticationStatus;
      })
      .addCase(authenticateForPdfUrlAccessCode.pending, (state, action) => {
        state.user.itemAssignmentCodeAuthenticationStatus = 'Pending';
        state.user.itemAssignmentCodeAuthenticated = false;
        state.user.pdfIdentifier = action.meta.arg.code;
      })
      .addCase(authenticateEmail.fulfilled, (state) => {
        state.isLoading = false;
        state.isLoggedIn = true;
        state.user.emailAuthenticationErrors = null;
        state.user.emailAuthenticationStatus = 'Success';
      })
      .addCase(authenticateEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.user.emailAuthenticationStatus = action.payload.emailAuthenticationStatus;
        state.user.emailAuthenticationErrors = action.payload.errors;
      })
      .addCase(authenticateEmail.pending, (state) => {
        state.isLoading = true;
        state.isLoggedIn = false;
        state.user.emailAuthenticated = false;
        state.user.emailAuthenticationErrors = null;
        state.user.emailAuthenticationStatus = 'Pending';
      })
      .addCase(authenticateSecurityCode.fulfilled, (state) => {
        state.isLoading = false;
        state.isLoggedIn = true;
        state.user.securityCodeAuthenticationStatus = 'Success';
      })
      .addCase(authenticateSecurityCode.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.user.securityCodeAuthenticationStatus = action.payload.securityCodeAuthenticationStatus;
      })
      .addCase(authenticateSecurityCode.pending, (state) => {
        state.isLoading = true;
        state.isLoggedIn = false;
        state.user.secretCodeAuthenticated = false;
        state.user.securityCodeAuthenticationStatus = 'Pending';
      })
      .addCase(authenticatePdfUrlAccessCode.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(authenticatePdfUrlAccessCode.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(authenticatePdfUrlAccessCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(syncWithSession.pending, (state) => {
        state.session = { ...state.session, syncCompleted: false };
      })
      .addDefaultCase(() => {});
  },
});

export default authSlice;
export const { reducer } = authSlice;
export const {
  emailAuthenticated,
  secretCodeAuthenticated,
  urlAccessCodeAuthenticated,
  sessionDataReceived,
  sessionSyncError,
  signatoryEmailReceived,
  sessionExpiredDetected,
} = authSlice.actions;
